import React, { useState } from "react";
import { Phone } from "@mui/icons-material";
import moment from "moment";
import { store } from "../../utils/store";
import { clickToCall } from "../Team/components/ShowAll/ShowAllService";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import { formatPhoneKeepingCountryCode } from "../../Shared/Phone/PhoneWithCountryCode";

type Props = {
  partner: any;
  onOpenDashboard?: () => void;
};

function normalizePhoneNumber(phone: any) {
  if (!phone) return "";
  const digitsOnly = String(phone).replace(/\D/g, "");
  if (digitsOnly.startsWith("91") && digitsOnly.length === 12) {
    return `+${digitsOnly}`;
  }
  if (digitsOnly.length === 10) {
    return `+91${digitsOnly}`;
  }
  if (!digitsOnly) return "";
  return `+${digitsOnly}`;
}

/** Right-side cold-call tray for channel partners (same Call UX as All Leads). */
const ChannelPartnerCallTray: React.FC<Props> = ({
  partner,
  onOpenDashboard,
}) => {
  const [calling, setCalling] = useState(false);
  const phone = partner?.phone || partner?.phoneNormalized || "";
  const phoneMasked =
    partner?.phoneMasked === true || String(phone).includes("*");
  const dialable = phoneMasked ? "" : normalizePhoneNumber(phone);
  const canCall = dialable.replace(/\D/g, "").length >= 10;
  const phoneDisplay = formatPhoneKeepingCountryCode(phone);

  const handleClickToCall = async () => {
    if (!canCall) {
      openErrorNotification("Valid phone number required to call");
      return;
    }
    try {
      setCalling(true);
      const payload = {
        leadPhone: dialable,
        uuid: store.getState().auth.uuid,
      };
      const response = await clickToCall(payload);
      if (response?.success === 0 || response?.success === false) {
        openErrorNotification(response?.message || "Unable to place call");
        return;
      }
      openSuccessNotification(response?.message || "Call initiated");
    } catch (e: any) {
      openErrorNotification(e?.message || "Unable to place call");
    } finally {
      setCalling(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h3 className="text-lg font-bold leading-tight text-[#111827]">
          {partner?.firmName || "Channel Partner"}
        </h3>
        <p className="mt-1 text-sm text-[#6B7280]">
          {partner?.updatedAt
            ? moment(partner.updatedAt).format("DD MMM, YYYY")
            : "—"}
        </p>
        {partner?.source || partner?.sourceType ? (
          <p className="mt-1 text-sm font-medium capitalize text-[#4338CA]">
            {partner.source ||
              (partner.sourceType === "manual"
                ? "Manual"
                : partner.sourceType === "bulk"
                  ? "Bulk"
                  : partner.sourceType)}
          </p>
        ) : null}
      </div>

      <button
        type="button"
        disabled={!canCall || calling}
        onClick={handleClickToCall}
        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#28a745] px-3 py-2.5 text-sm font-semibold text-white shadow-[0_2px_6px_rgba(0,0,0,0.15)] transition hover:bg-[#218838] disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Phone sx={{ fontSize: 18 }} />
        <span>{calling ? "Calling…" : "Call"}</span>
      </button>

      <div className="space-y-2.5 rounded-xl border border-[#EEF0F5] bg-[#F8FAFC] p-3 text-sm">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
            Phone
          </p>
          <p className="mt-0.5 font-mono font-medium tracking-wide text-[#111827]">
            {phoneDisplay}
          </p>
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
            Email
          </p>
          <p className="mt-0.5 break-all font-medium text-[#111827]">
            {partner?.email || "—"}
          </p>
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
            Grade
          </p>
          <p className="mt-0.5 font-medium text-[#111827]">
            {partner?.grade || "—"}
          </p>
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
            Leads
          </p>
          <p className="mt-0.5 font-medium text-[#111827]">
            {partner?.leadsCount ?? 0}
          </p>
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
            Status
          </p>
          <p className="mt-0.5 font-medium capitalize text-[#047857]">
            {partner?.status || "active"}
          </p>
        </div>
      </div>

      {onOpenDashboard ? (
        <button
          type="button"
          onClick={onOpenDashboard}
          className="w-full rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm font-semibold text-[#1E3A5F] hover:bg-[#F8FAFC]"
        >
          Open partner dashboard
        </button>
      ) : null}
    </div>
  );
};

export default ChannelPartnerCallTray;
